import { z } from 'zod';
import { SortOrder } from './helpers';
/**
 * Column data types supported by the table filtering system.
 * Used to determine which operators and input components are available.
 */
export const ColumnDataTypes = {
    STRING: 'string',
    TEXT: 'text',
    NUMBER: 'number',
    BOOLEAN: 'boolean',
    DATE: 'date',
    ENUM: 'enum',
};
/**
 * Zod schema for validating column data types.
 */
export const ColumnDataTypeSchema = z.enum([
    ColumnDataTypes.STRING,
    ColumnDataTypes.TEXT,
    ColumnDataTypes.NUMBER,
    ColumnDataTypes.BOOLEAN,
    ColumnDataTypes.DATE,
    ColumnDataTypes.ENUM,
]);
/**
 * Filter operators available for table filtering.
 */
export const FilterOperators = {
    EQUALS: 'equals',
    NOT_EQUALS: 'notEquals',
    INCLUDES: 'includes',
    EXCLUDES: 'excludes',
    CONTAINS: 'contains',
    NOT_CONTAINS: 'notContains',
    STARTS_WITH: 'startsWith',
    ENDS_WITH: 'endsWith',
    GREATER_THAN: 'greaterThan',
    LESS_THAN: 'lessThan',
    BETWEEN: 'between',
    IS_EMPTY: 'isEmpty',
    IS_NOT_EMPTY: 'isNotEmpty',
};
/** Operators that carry no value — their `value` array is always empty. */
export const VALUELESS_OPERATORS = [
    FilterOperators.IS_EMPTY,
    FilterOperators.IS_NOT_EMPTY,
];
/** True when an operator carries no value (Is Empty / Is Not Empty). */
export function isValuelessOperator(operator) {
    return VALUELESS_OPERATORS.includes(operator);
}
/**
 * Operators allowed on a date column: the range operators plus the value-less
 * ones (no equals/notEquals). Shared by every First Seen / Last Seen column
 * override so the date-filter vocabulary stays defined in one place.
 */
export const DATE_RANGE_OPERATORS = [
    FilterOperators.GREATER_THAN,
    FilterOperators.LESS_THAN,
    FilterOperators.BETWEEN,
    FilterOperators.IS_EMPTY,
    FilterOperators.IS_NOT_EMPTY,
];
/**
 * Zod schema for validating filter operators.
 */
export const FilterOperatorSchema = z.enum([
    FilterOperators.EQUALS,
    FilterOperators.NOT_EQUALS,
    FilterOperators.INCLUDES,
    FilterOperators.EXCLUDES,
    FilterOperators.CONTAINS,
    FilterOperators.NOT_CONTAINS,
    FilterOperators.STARTS_WITH,
    FilterOperators.ENDS_WITH,
    FilterOperators.GREATER_THAN,
    FilterOperators.LESS_THAN,
    FilterOperators.BETWEEN,
    FilterOperators.IS_EMPTY,
    FilterOperators.IS_NOT_EMPTY,
]);
/**
 * Individual filter value in a table query.
 * Value is always an array of strings for consistency:
 * - Single value operators (equals, contains): ['value']
 * - Multi-value operators (in, notIn): ['val1', 'val2', ...]
 * - Range operators (between): ['start', 'end']
 * - Empty operators (isEmpty, isNotEmpty): [] (empty array or ignored)
 */
export const FilterValueSchema = z.object({
    field: z.string(),
    operator: FilterOperatorSchema,
    value: z.array(z.string()),
});
/**
 * Array of filters for table queries.
 */
export const FiltersSchema = z.array(FilterValueSchema);
/**
 * Parse filter parameters from query string bracket notation.
 * Pattern: field[operator]=value1,value2
 *
 * @param query - Query parameters object from request
 * @param allowedFields - Array of field names that are allowed to be filtered
 * @param columnDataTypes - Optional map of field → data type. When provided,
 *   positional empties are preserved for NUMBER fields using a range operator
 *   (INCLUDES/EXCLUDES) so min-only `['10','']` and max-only `['','10']` stay
 *   distinct. Absent (or for any other field/operator) ⇒ legacy empty stripping.
 *
 * @example
 * Input: { "severity[includes]": "critical,high", "type[excludes]": "secret" }
 * Allowed: ["severity", "type"]
 * Output: [
 *   { field: "severity", operator: "includes", value: ["critical", "high"] },
 *   { field: "type", operator: "excludes", value: ["secret"] }
 * ]
 *
 * Security: Only fields in allowedFields are parsed. Unknown fields are ignored.
 * URL Encoding: Values are automatically decoded (e.g., %2C becomes comma)
 */
export function parseFilterParams(query, allowedFields, columnDataTypes) {
    if (allowedFields.length === 0) {
        return [];
    }
    const filters = [];
    // Create regex pattern from allowed fields: ^(field1|field2|field3)\[(operator)\]$
    const fieldsPattern = allowedFields
        .map(field => field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
    const operatorsPattern = Object.values(FilterOperators).join('|');
    const keyPattern = new RegExp(`^(${fieldsPattern})\\[(${operatorsPattern})\\]$`);
    Object.entries(query).forEach(([key, value]) => {
        const match = key.match(keyPattern);
        if (match && typeof value === 'string') {
            const field = match[1];
            const operator = match[2];
            // Split by comma and decode each value
            // decodeURIComponent handles values with special chars (brackets, commas, etc.)
            const rawValues = value.split(',').map(v => {
                const trimmed = v.trim();
                try {
                    return decodeURIComponent(trimmed);
                }
                catch {
                    // If decode fails, return original (already decoded by framework)
                    return trimmed;
                }
            });
            // Preserve positional empties only for numeric range operators so
            // min-only/max-only stay distinct; everything else strips as before.
            const isNumberRange = columnDataTypes?.[field] === ColumnDataTypes.NUMBER &&
                (operator === FilterOperators.INCLUDES ||
                    operator === FilterOperators.EXCLUDES);
            const values = isNumberRange ? rawValues : rawValues.filter(Boolean);
            if (isValuelessOperator(operator)) {
                filters.push({ field, operator, value: [] });
            }
            else if (values.some(Boolean)) {
                filters.push({
                    field,
                    operator,
                    value: values,
                });
            }
        }
    });
    return filters;
}
/**
 * Create a Zod preprocessor that parses filters from query params.
 * Use this in your query schema to automatically parse bracket notation filters.
 *
 * @param allowedFields - Array of field names that can be filtered
 * @returns Zod schema that transforms query params to FilterValue[]
 *
 * @example
 * const querySchema = z.object({
 *   page: z.coerce.number(),
 *   ...createFilterParamsSchema(['severity', 'type', 'status'])
 * });
 */
export function createFilterParamsSchema(allowedFields) {
    return z.preprocess(query => parseFilterParams(query, allowedFields), FiltersSchema.optional().default([]));
}
/**
 * Encode a search string for safe use in a query parameter value.
 * Named wrapper for encodeURIComponent to make intent explicit and testable.
 */
export function encodeSearchParam(search) {
    return encodeURIComponent(search);
}
/**
 * Decode a search string from a query parameter value.
 * Named wrapper for decodeURIComponent to make intent explicit and testable.
 */
export function decodeSearchParam(search) {
    return decodeURIComponent(search);
}
/**
 * Serialize table query params into a query string.
 * - search is explicitly encoded via encodeSearchParam
 * - filters are encoded with bracket notation (field[operator]=val1,val2)
 * - all other scalar values are encodeURIComponent encoded
 * - arrays are joined with commas, each element encoded
 *
 * Use as the Axios `paramsSerializer` for any DataTable API call.
 *
 * Takes `object` rather than `Record<string, unknown>`: since axios 1.20 the
 * serializer's param type is inferred from the request's `params`, and
 * interface-typed params have no implicit index signature.
 */
export function serializeTableQueryParams(params) {
    const { filters, search, ...rest } = params;
    const parts = [];
    if (search !== undefined && search !== '') {
        parts.push(`search=${encodeSearchParam(search)}`);
    }
    Object.entries(rest).forEach(([key, value]) => {
        if (value === null || value === undefined)
            return;
        if (Array.isArray(value)) {
            const joined = value
                .map(item => encodeURIComponent(String(item)))
                .join(',');
            if (joined.length > 0)
                parts.push(`${key}=${joined}`);
            return;
        }
        parts.push(`${key}=${encodeURIComponent(String(value))}`);
    });
    if (filters?.length) {
        Object.entries(encodeFiltersToQueryParams(filters)).forEach(([key, value]) => {
            parts.push(`${key}=${value}`);
        });
    }
    return parts.join('&');
}
/**
 * Factory that wraps a base ZodObject schema with the standard
 * `.loose().transform()` required for all DataTable query schemas.
 *
 * The transform handles two input shapes:
 * 1. Pre-parsed `filters` array (from API client sending a POST body or typed params)
 * 2. Bracket-notation query params (from Fastify query string: field[operator]=val)
 *
 * `columnDataTypes` is the single source of truth:
 * - Keys   → allowed filter fields (unknown fields are silently dropped)
 * - Values → ColumnDataType, which determines valid operators
 *
 * `columnOperatorOverrides` (optional): narrows allowed operators per column; cannot widen beyond data-type defaults.
 *
 * @example
 * export const myQuerySchema = createTableQuerySchema(
 *   paginationSchema.extend({ sortBy: sortFieldSchema.optional() }),
 *   { name: ColumnDataTypes.STRING, status: ColumnDataTypes.ENUM }
 * );
 */
export function createTableQuerySchema(baseSchema, columnDataTypes, columnOperatorOverrides) {
    const allowedFields = Object.keys(columnDataTypes);
    const validatedFilterSchema = createValidatedFilterSchema(columnDataTypes, columnOperatorOverrides);
    const filtersWithValidation = FiltersSchema.superRefine((filters, ctx) => {
        filters.forEach((filter, index) => {
            if (!allowedFields.includes(filter.field)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Unsupported filter field',
                    path: [index, 'field'],
                });
                return;
            }
            if (!validatedFilterSchema.safeParse(filter).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Unsupported operator for filter field',
                    path: [index, 'operator'],
                });
                return;
            }
            // NUMBER value is [min, max]; map shared range flags to Zod issues.
            const needsNumericRangeChecks = columnDataTypes[filter.field] === ColumnDataTypes.NUMBER &&
                !isValuelessOperator(filter.operator);
            if (needsNumericRangeChecks) {
                const range = evaluateNumericFilterRange(filter.value);
                // Reject legacy single-value / IN-list forms loudly.
                if (!range.hasValidShape) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Numeric filters require two positional values [min, max]; either bound may be empty.',
                        path: [index, 'value'],
                    });
                    return;
                }
                if (!range.isMinSet && !range.isMaxSet) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Enter a minimum or maximum numeric value.',
                        path: [index, 'value'],
                    });
                    return;
                }
                if (!range.isMinValid) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Numeric filter values must be non-negative integers.',
                        path: [index, 'value', 0],
                    });
                }
                if (!range.isMaxValid) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Numeric filter values must be non-negative integers.',
                        path: [index, 'value', 1],
                    });
                }
                if (!range.isMinValid || !range.isMaxValid) {
                    return;
                }
                if (!range.isOrdered) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Minimum value must be less than or equal to maximum value.',
                        path: [index, 'value'],
                    });
                }
            }
        });
    });
    return baseSchema.loose().transform((data, ctx) => {
        const record = data;
        if (record.filters !== undefined) {
            const filtersResult = filtersWithValidation.safeParse(record.filters ?? []);
            if (!filtersResult.success) {
                filtersResult.error.issues.forEach(issue => ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: issue.message,
                    path: issue.path,
                }));
                return z.NEVER;
            }
            return { ...data, filters: filtersResult.data };
        }
        const parsedFilters = parseFilterParams(record, allowedFields, columnDataTypes);
        const filtersResult = filtersWithValidation.safeParse(parsedFilters);
        if (!filtersResult.success) {
            filtersResult.error.issues.forEach(issue => ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: issue.message,
                path: issue.path,
            }));
            return z.NEVER;
        }
        return { ...data, filters: filtersResult.data };
    });
}
/**
 * Encode filters to query parameters with bracket notation.
 * Values are URL-encoded to safely handle special characters.
 *
 * @param filters - Array of filter values
 * @returns Object with bracket notation keys
 *
 * @example
 * Input: [{ field: "severity", operator: "includes", value: ["critical", "high"] }]
 * Output: { "severity[includes]": "critical,high" }
 */
export function encodeFiltersToQueryParams(filters) {
    const params = {};
    filters.forEach(filter => {
        // Key structure is not encoded - it defines the param format
        const key = `${filter.field}[${filter.operator}]`;
        // Values are encoded to handle special characters (brackets, commas, etc.)
        const encodedValues = filter.value.map(v => encodeURIComponent(v));
        params[key] = encodedValues.join(',');
    });
    return params;
}
/**
 * Mapping of data types to their valid operators.
 * Used for validation and UI generation.
 *
 * Value-less ops (IS_EMPTY/IS_NOT_EMPTY) are allowed per data type here; a column
 * opts in by listing them in its filterOperators. Per-column overrides can only
 * narrow this list, so enabling a data type happens at this shared map — it widens
 * for every table by design, not just the one being worked on.
 */
export const OPERATORS_BY_DATA_TYPE = {
    [ColumnDataTypes.STRING]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
        FilterOperators.STARTS_WITH,
        FilterOperators.ENDS_WITH,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [ColumnDataTypes.TEXT]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
        FilterOperators.STARTS_WITH,
        FilterOperators.ENDS_WITH,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [ColumnDataTypes.NUMBER]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [ColumnDataTypes.BOOLEAN]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
    ],
    [ColumnDataTypes.DATE]: [
        FilterOperators.EQUALS,
        FilterOperators.NOT_EQUALS,
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [ColumnDataTypes.ENUM]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
/**
 * Validates that an operator is valid for a given data type.
 */
export function isValidOperatorForDataType(dataType, operator) {
    return OPERATORS_BY_DATA_TYPE[dataType].includes(operator);
}
/**
 * Gets the list of valid operators for a given data type.
 */
export function getOperatorsForDataType(dataType) {
    return OPERATORS_BY_DATA_TYPE[dataType];
}
// Validates a single bound: a non-negative integer (digits only, so `Number()`
// edge cases like 3.14/0x1f/1e5 are rejected). Empty is invalid unless allowEmpty.
export function isValidNumericFilterValue(value, options) {
    const trimmed = value.trim();
    if (trimmed === '') {
        return options?.allowEmpty ?? false;
    }
    return /^\d+$/.test(trimmed);
}
// Shared source of truth for NUMBER range rules: query schema, filter form, URL.
export function evaluateNumericFilterRange(value) {
    const hasValidShape = value.length === 2;
    const minValue = (value[0] ?? '').trim();
    const maxValue = (value[1] ?? '').trim();
    const isMinSet = minValue !== '';
    const isMaxSet = maxValue !== '';
    const isMinValid = isValidNumericFilterValue(minValue, { allowEmpty: true });
    const isMaxValid = isValidNumericFilterValue(maxValue, { allowEmpty: true });
    const isOrdered = !isMinSet || !isMaxSet || !isMinValid || !isMaxValid
        ? true
        : Number(minValue) <= Number(maxValue);
    return {
        hasValidShape,
        isMinValid,
        isMaxValid,
        isMinSet,
        isMaxSet,
        isOrdered,
    };
}
// Boolean wrapper for callers that only need accept/drop. Single definition of "valid".
export function isValidNumericFilterRange(value) {
    const range = evaluateNumericFilterRange(value);
    return (range.hasValidShape &&
        (range.isMinSet || range.isMaxSet) &&
        range.isMinValid &&
        range.isMaxValid &&
        range.isOrdered);
}
/**
 * Schema for validating a filter value with its data type context.
 * Overrides narrow the data-type defaults; they cannot allow operators the data type rejects.
 */
export function createValidatedFilterSchema(columnDataTypes, columnOperatorOverrides) {
    return FilterValueSchema.refine(filter => {
        const dataType = columnDataTypes[filter.field];
        if (!dataType) {
            return false; // Unknown column
        }
        if (!isValidOperatorForDataType(dataType, filter.operator)) {
            return false;
        }
        const override = columnOperatorOverrides?.[filter.field];
        return override ? override.includes(filter.operator) : true;
    }, {
        message: 'Invalid operator for column data type',
        path: ['operator'],
    });
}
/**
 * Zod schema for PaginationMeta validation.
 */
export const PaginationMetaSchema = z.object({
    page: z.number().int().min(0),
    limit: z.number().int().min(1),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
});
/**
 * Pagination block for DataTable response wrappers (`page` is 1-based here).
 * Shared so per-table response schemas reference it instead of re-declaring the shape.
 */
export const tableResponsePaginationSchema = z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
});
/**
 * Zod schema for ActiveFilterMeta validation.
 */
export const ActiveFilterMetaSchema = z.object({
    field: z.string(),
    operator: z.string(),
    values: z.array(z.unknown()),
});
/**
 * Zod schema for TableResponseMeta validation.
 *
 * Uses `.passthrough()` to allow future server-side meta fields (e.g. totalFilters)
 * without breaking client-side parsing. Tighter validation is applied at the
 * API client level where needed.
 */
export const TableResponseMetaSchema = z
    .object({
    activeFilters: z.array(ActiveFilterMetaSchema),
    sort: z
        .object({
        field: z.string(),
        order: z.nativeEnum(SortOrder),
    })
        .optional(),
    search: z
        .object({
        value: z.string(),
        fields: z.array(z.string()),
    })
        .optional(),
    // KIR-3429: set when a handler had to cap the result set it could order
    // (e.g. the in-memory logged-in-user sort), so the row count is below the
    // true filtered total. Lets the client flag/disable pagination past the cap.
    truncated: z.boolean().optional(),
})
    .passthrough();
/**
 * Factory function to create a TableResponse with proper typing.
 *
 * Usage:
 * ```typescript
 * const response = createTableResponse<Alert>(alerts, {
 *   page: 1,
 *   limit: 20,
 *   total: 100,
 *   hasMore: true,
 * }, { activeFilters: [] });
 * ```
 */
export function createTableResponse(data, pagination, meta) {
    return {
        data,
        pagination,
        meta,
    };
}
//# sourceMappingURL=table.js.map